/**
 * Expired token state.
 * Includes clear recovery steps for AAA accessibility compliance.
 * Focus management for WCAG 2.4.3 compliance.
 */

import * as React from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import { Clock, Mail, CheckCircle2 } from "lucide-react";

export function ExpiredTokenState() {
  const { t } = useTranslation();
  const headingRef = React.useRef<HTMLHeadingElement>(null);

  // Focus on heading when component mounts for screen reader users
  React.useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div className="w-full py-6 sm:py-12 px-0 sm:px-4">
      <div className="mx-auto max-w-lg">
        <Card className="border-warning/50 rounded-none sm:rounded-lg border-x-0 sm:border-x">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-warning/10" aria-hidden="true">
              <Clock className="h-8 w-8 text-warning" />
            </div>
            <CardTitle ref={headingRef} tabIndex={-1} as="h1" className="text-2xl text-warning outline-none">
              {t("onboarding.token.expiredTitle")}
            </CardTitle>
            <CardDescription>
              {t("onboarding.token.expiredDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Recovery steps for AAA compliance */}
            <div className="rounded-lg border border-muted p-4" role="region" aria-labelledby="recovery-title">
              <h2 id="recovery-title" className="text-sm font-medium mb-3 flex items-center gap-2">
                <Mail className="h-4 w-4" aria-hidden="true" />
                {t("onboarding.token.recoveryTitle", "How to get a new link:")}
              </h2>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" aria-hidden="true" />
                  <span>{t("onboarding.token.recoveryStep1", "Contact your company administrator")}</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" aria-hidden="true" />
                  <span>{t("onboarding.token.recoveryStep2", "Request a new onboarding invitation")}</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" aria-hidden="true" />
                  <span>{t("onboarding.token.recoveryStep3", "Check your email for the new link")}</span>
                </li>
              </ul>
            </div>

            <div className="rounded-lg bg-muted p-4">
              <p className="text-sm text-muted-foreground">
                {t("onboarding.token.requestNewLink")}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
